/**
 * AliasDetailPage — 别名详情
 * 展示别名完整信息；删除操作放在此处（列表行不再直接提供）。
 */
import { useState } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  makeStyles,
  tokens,
  Text,
  Button,
  Badge,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
} from '@fluentui/react-components'
import { DeleteRegular, MailArrowForwardRegular } from '@fluentui/react-icons'
import { aliasesApi } from '@/api/aliases'
import type { Alias } from '@/api/types'
import { toast } from '@/lib/toast'
import { ApiError } from '@/api/client'
import { ROUTES } from '@/routes'
import DetailLayout, { DetailSection, DetailRow } from '@/components/DetailLayout'
import DangerButton from '@/components/DangerButton'
import { useDetailItem } from '@/hooks/useDetailItem'

const useStyles = makeStyles({
  dialogActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    paddingTop: '8px',
  },
  deleteName: {
    fontWeight: 600,
    color: tokens.colorStatusDangerForeground1,
  },
})

export default function AliasDetailPage() {
  const styles = useStyles()
  const { id } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { item, isLoading } = useDetailItem<Alias>(
    ['aliases'],
    () => aliasesApi.list(),
    id as string,
    'id',
  )

  const [deleteOpen, setDeleteOpen] = useState(false)

  const deleteMutation = useMutation({
    mutationFn: () => aliasesApi.delete(Number(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['aliases'] })
      toast.success('删除成功', '别名已删除')
      navigate(ROUTES.ALIASES)
    },
    onError: (err) =>
      toast.error('删除失败', err instanceof ApiError ? err.message : '删除失败'),
  })

  const stateItem = (location.state as { item?: Alias } | null)?.item

  return (
    <>
      <DetailLayout
        title={`${(item ?? stateItem)?.address}@${(item ?? stateItem)?.domain}`}
        subtitle="邮件别名"
        media={<MailArrowForwardRegular fontSize={22} />}
        badge={
          <Badge appearance="filled" color={(item ?? stateItem)?.active ? 'success' : 'danger'}>
            {(item ?? stateItem)?.active ? '活跃' : '停用'}
          </Badge>
        }
        loading={isLoading && !stateItem}
        notFound={!isLoading && !item && !stateItem}
        notFoundText="未找到该别名"
        actions={
          <Button
            appearance="subtle"
            icon={<DeleteRegular />}
            onClick={() => setDeleteOpen(true)}
          >
            删除
          </Button>
        }
      >
        <DetailSection title="基本信息">
          <DetailRow label="ID" value={item?.id ?? stateItem?.id} />
          <DetailRow
            label="地址"
            value={
              item
                ? `${item.address}@${item.domain}`
                : stateItem
                  ? `${stateItem.address}@${stateItem.domain}`
                  : ''
            }
          />
          <DetailRow label="目标地址" value={item?.target ?? stateItem?.target} />
          <DetailRow
            label="状态"
            value={(item ?? stateItem)?.active ? '活跃' : '停用'}
          />
        </DetailSection>
      </DetailLayout>

      {/* 删除确认 */}
      <Dialog
        open={deleteOpen}
        onOpenChange={(_, d) => {
          if (!d.open) setDeleteOpen(false)
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>确认删除</DialogTitle>
            <DialogContent>
              <Text>
                确定要删除别名{' '}
                <span className={styles.deleteName}>
                  {(item ?? stateItem)
                    ? `${(item ?? stateItem)?.address}@${(item ?? stateItem)?.domain}`
                    : ''}
                </span>{' '}
                吗？此操作不可撤销。
              </Text>
            </DialogContent>
            <div className={styles.dialogActions}>
              <Button onClick={() => setDeleteOpen(false)}>取消</Button>
              <DangerButton
                loading={deleteMutation.isPending}
                onClick={() => deleteMutation.mutate()}
              >
                确认删除
              </DangerButton>
            </div>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  )
}
